import { PayloadAction } from "@reduxjs/toolkit";
import parse from "format-message-parse";
import { put, takeLatest, call, all, select } from "redux-saga/effects";

import { BannerStatus } from "@plex/react-components";

import { HttpStatusCodes } from "infrastructure/http";
import { commonMessages } from "infrastructure/localization";
import { notification } from "infrastructure/notification";
import { displayMessages } from "ui/screens/translation-managers";
import { extractApiErrorMessage } from "ui/shared/models/common";

import { messageFormatActions } from "./MessageId.actions";
import { getMessageData, addMessage, updateMessage, approveMessage, obsoleteMessage, deprecateMessage } from "./MessageId.api";
import { getTokenParameters } from "./MessageId.helpers";
import { IMessageResponse, IMessageIdState, IToken } from "./MessageId.state";

function* loadMessageDataExecute({ payload }: PayloadAction<string>) {
  try {
    const messageDetails: IMessageResponse = yield call(getMessageData, payload);
    try {
      const textElements: parse.Element[] = parse(messageDetails.text);
      const messageTokens: IToken[] = getTokenParameters(textElements);
      yield put(messageFormatActions.loadMessageDataCompleted({ response: messageDetails, messageTokens: messageTokens }));
    } catch (error) {
      yield put(messageFormatActions.setValidationBanner(true));
    }
  } catch (error) {
    yield put(messageFormatActions.loadMessageDataFailed(notification.error().useMessageId(commonMessages.error("unexpectedError"))));
  }
}

function* addMessageIdExecute(action: PayloadAction<string>) {
  const messageIdState: IMessageIdState = yield select((state: { messageFormat: IMessageIdState }) => state.messageFormat);
  try {
    yield call(
      addMessage,
      {
        messageId: messageIdState.messageId,
        text: messageIdState.text,
        tokens: messageIdState.tokens
      },
      messageIdState.version
    );

    yield put(
      messageFormatActions.addMessageIdCompleted({
        redirectPath: action.payload,
        notification: notification.create(BannerStatus.success).useMessageId(displayMessages.info("addMessageIdCompleted"))
      })
    );
  } catch (error) {
    yield put(
      messageFormatActions.addMessageIdFailed(
        error.statusCode === HttpStatusCodes.BadRequest
          ? notification.error().useMessage(extractApiErrorMessage(error.data).localizable)
          : notification.error().useMessageId(commonMessages.error("unexpectedError"))
      )
    );
  }
}

function* updateMessageIdExecute(action: PayloadAction<string>) {
  const messageIdState: IMessageIdState = yield select((state: { messageFormat: IMessageIdState }) => state.messageFormat);
  try {
    yield call(
      updateMessage,
      {
        messageId: messageIdState.messageId,
        text: messageIdState.text,
        tokens: messageIdState.tokens
      },
      messageIdState.version
    );

    yield put(
      messageFormatActions.updateMessageIdCompleted({
        redirectPath: action.payload,
        notification: notification.success().useMessageId(displayMessages.info("updateMessageIdCompleted"))
      })
    );
  } catch (error) {
    yield put(
      messageFormatActions.updateMessageIdFailed(
        error.statusCode === HttpStatusCodes.BadRequest
          ? notification.error().useMessage(extractApiErrorMessage(error.data).localizable)
          : notification.error().useMessageId(commonMessages.error("unexpectedError"))
      )
    );
  }
}

function* approveMessageExecute({ payload }: PayloadAction<string>) {
  const messageIdState: IMessageIdState = yield select((state: { messageFormat: IMessageIdState }) => state.messageFormat);

  try {
    yield call(approveMessage, payload, messageIdState.version);
    yield put(
      messageFormatActions.updateStatusCompleted({
        newStatus: displayMessages.option("messageStatusPrerelease").value,
        notification: notification.success().useMessageId(displayMessages.info("messageApproveCompleted"))
      })
    );
  } catch (error) {
    yield put(
      messageFormatActions.updateStatusFailed(
        error.statusCode === HttpStatusCodes.BadRequest
          ? notification.error().useMessage(extractApiErrorMessage(error.data).localizable)
          : notification.error().useMessageId(commonMessages.error("unexpectedError"))
      )
    );
  }
}

function* obsoleteMessageExecute({ payload }: PayloadAction<string>) {
  const messageIdState: IMessageIdState = yield select((state: { messageFormat: IMessageIdState }) => state.messageFormat);

  try {
    yield call(obsoleteMessage, payload, messageIdState.version);
    yield put(
      messageFormatActions.updateStatusCompleted({
        newStatus: displayMessages.option("messageStatusObsolete").value,
        notification: notification.success().useMessageId(displayMessages.info("messageObsoleteCompleted"))
      })
    );
  } catch (error) {
    yield put(
      messageFormatActions.updateStatusFailed(
        error.statusCode === HttpStatusCodes.BadRequest
          ? notification.error().useMessage(extractApiErrorMessage(error.data).localizable)
          : notification.error().useMessageId(commonMessages.error("unexpectedError"))
      )
    );
  }
}

function* deprecateMessageExecute({ payload }: PayloadAction<string>) {
  const messageIdState: IMessageIdState = yield select((state: { messageFormat: IMessageIdState }) => state.messageFormat);

  try {
    yield call(deprecateMessage, payload, messageIdState.version);
    yield put(
      messageFormatActions.updateStatusCompleted({
        newStatus: displayMessages.option("messageStatusDeprecated").value,
        notification: notification.success().useMessageId(displayMessages.info("messageDeprecateCompleted"))
      })
    );
  } catch (error) {
    yield put(
      messageFormatActions.updateStatusFailed(
        error.statusCode === HttpStatusCodes.BadRequest
          ? notification.error().useMessage(extractApiErrorMessage(error.data).localizable)
          : notification.error().useMessageId(commonMessages.error("unexpectedError"))
      )
    );
  }
}

function* actionWatcher() {
  yield takeLatest(messageFormatActions.loadMessageData.toString(), loadMessageDataExecute);
  yield takeLatest(messageFormatActions.addMessageId.toString(), addMessageIdExecute);
  yield takeLatest(messageFormatActions.updateMessageId.toString(), updateMessageIdExecute);
  yield takeLatest(messageFormatActions.approveMessage.toString(), approveMessageExecute);
  yield takeLatest(messageFormatActions.obsoleteMessage.toString(), obsoleteMessageExecute);
  yield takeLatest(messageFormatActions.deprecateMessage.toString(), deprecateMessageExecute);
}

export function* appSaga() {
  yield all([actionWatcher()]);
}
